/**
 * Core WebSocket Envelope Schema (Zod)
 *
 * The generic `WebSocketService` (`websocket.service.ts`) is the single
 * `JSON.parse(event.data)` boundary for every endpoint multiplexed through
 * the singleton (e.g. `trading`, `risk`, `alerts`, `market-data`,
 * `metrics/live`). Per-payload validation lives in each consumer service —
 * the generic service only validates that the wire envelope itself is shaped
 * `{ type, data|payload, timestamp?, source? }` (and is forgiving about
 * additive BE fields).
 *
 * GAP-FE-003 — adopt the dashboard `safeParse` pattern across all WS
 * services.
 *
 * Notes
 * - We intentionally allow `data: z.unknown()` here. Tighter validation must
 *   happen in the consumer service that knows the message taxonomy.
 * - `type` is `z.string()` (not `z.enum`) because the same singleton handles
 *   ~40 distinct message kinds across modules; keeping it as `string` keeps
 *   the validator stable when new kinds are added BE-side.
 * - `timestamp` is accepted as either an ISO string or a number (epoch ms /
 *   epoch seconds depending on the BE handler).
 */

import { number, object, string, union, unknown, type infer as ZodInfer } from 'zod';

/**
 * Generic envelope sent on the wire.
 *
 * The actual `WebSocketMessage` type used internally has `timestamp: Date`
 * (already converted) and `source: string` (set from the endpoint). This
 * schema models the WIRE shape as the BE emits it, before service-side
 * normalisation.
 */
export const WebSocketEnvelopeWireSchema = object({
  type: string().optional(),
  data: unknown().optional(),
  payload: unknown().optional(),
  timestamp: union([string(), number()]).optional(),
  source: string().optional(),
}).passthrough();

export type WebSocketEnvelopeWire = ZodInfer<typeof WebSocketEnvelopeWireSchema>;
